import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { useLabels } from "@/core/context/LabelsContext"
import ROUTE_NAMES from "@/core/route/util/routeNames"
import DashboardBlockEmptyState from "@/dashboard/blocks/kinds/DashboardBlockEmptystate"
import { useDashboardContext } from "@/dashboard/context/DashboardContext"
import { ProductCardSkeleton } from "@/product/card/ProductCard"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import {
  DiscoCarousel,
  DiscoCarouselSkeleton,
  DiscoIcon,
  DiscoLink,
  DiscoText,
} from "@disco-ui"
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay"
import DashboardBlockAdminDropdown from "../../DashboardBlockAdminDropdown"
import ContinueYourProductCard from "./ContinueYourProductCard"
import { ContinueYourProductsDashboardBlockContentPaginationFragment$key } from "./__generated__/ContinueYourProductsDashboardBlockContentPaginationFragment.graphql"
import {
  ContinueYourProductsDashboardBlockContentQuery,
  DashboardBlockPosition,
} from "./__generated__/ContinueYourProductsDashboardBlockContentQuery.graphql"
import { ContinueYourProductsDashboardBlockFragment$data } from "./__generated__/ContinueYourProductsDashboardBlockFragment.graphql"
import { ContinueYourProductsDashboardBlockPaginationQuery } from "./__generated__/ContinueYourProductsDashboardBlockPaginationQuery.graphql"

interface Props {
  block: ContinueYourProductsDashboardBlockFragment$data
}

function ContinueYourProductsDashboardBlockContent({ block }: Props) {
  const labels = useLabels()
  const activeOrganization = useActiveOrganization()
  const canManage = activeOrganization?.viewerPermissions.has("dashboard.manage")
  const { isOneColumn } = useDashboardContext()!
  const classes = useStyles()

  const { continueYourProductsBlock } =
    useLazyLoadQuery<ContinueYourProductsDashboardBlockContentQuery>(
      graphql`
        query ContinueYourProductsDashboardBlockContentQuery($id: ID!, $first: Int) {
          continueYourProductsBlock: node(id: $id) {
            ... on ContinueYourProductsDashboardBlock {
              title
              position
              ...ContinueYourProductsDashboardBlockContentPaginationFragment
                @arguments(first: $first)
            }
          }
        }
      `,
      {
        id: block.id,
        first: 3,
      },
      {
        fetchPolicy: "network-only",
      }
    )

  const { data, hasNext, hasPrevious, loadNext, isLoadingNext } = usePaginationFragment<
    ContinueYourProductsDashboardBlockPaginationQuery,
    ContinueYourProductsDashboardBlockContentPaginationFragment$key
  >(
    graphql`
      fragment ContinueYourProductsDashboardBlockContentPaginationFragment on ContinueYourProductsDashboardBlock
      @refetchable(queryName: "ContinueYourProductsDashboardBlockPaginationQuery")
      @argumentDefinitions(first: { type: "Int" }, after: { type: "String" }) {
        id
        products(first: $first, after: $after)
          @connection(key: "ContinueYourProductsDashboardBlockContent__products") {
          __id
          totalCount
          edges {
            node {
              id
              slug
              todos
              ...ContinueYourProductCardFragment
            }
          }
        }
      }
    `,
    continueYourProductsBlock
  )

  // jump back in empty state
  const todoProducts = Relay.connectionToArray(data?.products)
  if (!todoProducts.length)
    return (
      <div className={classes.emptyStateContainer}>
        <div className={classes.emptyStateTitle}>
          <DiscoText variant={"body-md-700"}>{"Jump Back In"}</DiscoText>
          <DashboardBlockAdminDropdown dashboardBlockKey={block} />
        </div>
        <DashboardBlockEmptyState
          testid={"DashboardBlockEmptyState.continue_your_products"}
          icon={<DiscoIcon icon={"3d-box"} />}
          subtitle={
            canManage
              ? `There are (0) ${labels.admin_experience.plural} available.`
              : `You're not registered for any ${labels.experience.plural}.`
          }
          buttons={
            <DiscoLink
              textVariant={"body-sm-600"}
              to={ROUTE_NAMES.COMMUNITY.EXPERIENCES.ROOT}
              className={classes.emptyStateLink}
            >
              {canManage
                ? `Create New ${labels.admin_experience.singular}`
                : `Explore ${labels.experience.plural}`}
            </DiscoLink>
          }
        />
      </div>
    )

  return (
    <DiscoCarousel
      testid={"ContinueYourProductsDashboardBlockContent"}
      slidesPerView={getSlidesPerView(isOneColumn, block.position)}
      centeredSlides={false}
      title={continueYourProductsBlock?.title}
      classes={{ header: classes.header }}
      moreActions={<DashboardBlockAdminDropdown dashboardBlockKey={block} />}
      refetch={{
        hasNext,
        hasPrevious,
        loadMore: () => loadNext(3),
        isLoading: isLoadingNext,
      }}
      totalCount={data?.products.totalCount || 0}
      data={todoProducts}
      item={(product) => {
        return (
          <ContinueYourProductCard
            blockId={block.id}
            productKey={product}
            variant={isOneColumn ? "wide" : "tall"}
          />
        )
      }}
    />
  )
}
const useStyles = makeUseStyles((theme) => ({
  emptyStateContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: `${theme.spacing(2)}px ${theme.spacing(2)}px ${theme.spacing(0.5)}px`,
  },
  emptyStateTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  emptyStateLink: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  header: {
    marginBottom: 0,
  },
}))

function getSlidesPerView(isOneColumn: boolean, position: DashboardBlockPosition) {
  if (isOneColumn) return 1.1
  return position === "main" ? 2.2 : 1
}

export function ContinueYourProductsDashboardBlockContentSkeleton({ block }: Props) {
  const { isOneColumn } = useDashboardContext()!

  return (
    <DiscoCarouselSkeleton
      showTitle
      item={<ProductCardSkeleton coverPosition={isOneColumn ? "left" : "top"} />}
      slidesPerView={getSlidesPerView(isOneColumn, block.position)}
    />
  )
}

export default Relay.withSkeleton({
  component: ContinueYourProductsDashboardBlockContent,
  skeleton: ContinueYourProductsDashboardBlockContentSkeleton,
})
